import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { formatDate } from 'helpers/helpers'
import { checkLinks, STATUS_ICONS } from './checkCatalog'

// Строка проверки: что проверяли, что вышло и что с этим делать.
//
// Заголовок и объяснение печатаются по коду проверки (`setup_health_check_<code>`,
// `setup_health_fix_<code>`) — бэк текстов не присылает. `count` подставляется в текст
// через {{count}}: «37 товаров без МХИК».
function HealthCheckRow({ check }) {
	const { t } = useTranslation()

	// Проблемные строки разворачиваем сразу: за красным обычно и приходят
	const [isOpen, setIsOpen] = useState(check.status === 'error')

	const links = checkLinks(check.code)
	const isProblem = check.status === 'error' || check.status === 'warning'

	// В описании проверки может лежать дата — «касса на связи», «последний заказ»,
	// «последняя синхронизация». Из базы она приходит как 2026-09-13 19:52:36.414049,
	// и показывать её клиенту в таком виде нельзя
	function prettyDescription(value) {
		if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(value)) {
			return formatDate(value, 'dd.MM.yyyy HH:mm')
		}

		return value
	}

	// У пройденной проверки описание нужно не всегда: «Наличка, Карта» полезно,
	// а «всё хорошо» второй раз читать незачем
	// У одной проверки бывает несколько причин (meta.reason): «принтеров нет» и «не отвечают» —
	// разные тексты. Ключ причины пробуем первым, общий ключ проверки — запасной
	const reason = check.meta?.reason
	const description = isProblem
		// total приходит в meta: «не отвечают 2 из 5» читается, а «не отвечают: 2» — нет
		? t([...(reason ? [`setup_health_fix_${check.code}_${reason}`] : []), `setup_health_fix_${check.code}`], {
			count: check.count ?? 0,
			total: check.meta?.total ?? 0,
			description: check.description ?? '',
		})
		: prettyDescription(check.description)

	// Пояснение к пропущенной проверке: «услуга не подключена», «касса не ответила»,
	// «драйвер не сообщает состояние смены». Без него серая строка читается
	// как «сломалось, но непонятно что»
	const skippedReason = check.status === 'skipped'
		? t([...(reason ? [`setup_health_skipped_${check.code}_${reason}`] : []), `setup_health_skipped_${check.code}`], {
			defaultValue: t('setup_health_skipped_default'),
			count: check.count ?? 0,
			description: prettyDescription(check.description) ?? '',
		})
		: null

	// Разворачивать есть что, только если под строкой что-то есть
	const hasDetails = Boolean(description || skippedReason || links.doc || (isProblem && links.fix))

	return (
		<div className="py-2 border-bottom">
			{/* Свёрнутая строка — только галочка и название. Кнопка «Документация» у каждой
				из одиннадцати проверок превращала окно в частокол кнопок, поэтому подробности
				и ссылки открываются кликом по строке */}
			<div className={`d-flex align-items-start ${hasDetails ? 'cursor-pointer' : ''}`}
				onClick={() => hasDetails && setIsOpen(!isOpen)}>
				<i className={`uil ${STATUS_ICONS[check.status] || STATUS_ICONS.skipped} me-2 mt-1`}></i>

				<div className="w-100">
					<div className="d-flex justify-content-between">
						<span className={isProblem ? 'fw-500' : (check.status === 'skipped' ? 'text-muted' : '')}>
							{t(`setup_health_check_${check.code}`)}
						</span>

						{hasDetails &&
							<i className={`uil ${isOpen ? 'uil-angle-up' : 'uil-angle-down'} text-muted`}></i>
						}
					</div>

					{/* Проблему прячем только наполовину: строку «не отвечают 2 из 5» видно
						сразу, а кнопки и документация — по клику. Ошибка не должна требовать
						лишнего действия, чтобы её прочитали */}
					{isProblem && description &&
						<div className="fs-6 text-danger">{description}</div>
					}

					{isOpen &&
						<>
							{!isProblem && description &&
								<div className="fs-6 text-muted">{description}</div>
							}

							{skippedReason &&
								<div className="fs-6 text-muted">{skippedReason}</div>
							}

							{(links.doc || (isProblem && links.fix)) &&
								<div className="d-flex gap-2 mt-1">
									{isProblem && links.fix &&
										<Link className="btn btn-sm btn-outline-primary" to={links.fix}>
											<i className="uil uil-wrench me-1"></i>
											{t('fix')}
										</Link>
									}

									{links.doc &&
										<a className="btn btn-sm btn-outline-secondary" href={links.doc} target="_blank" rel="noreferrer">
											<i className="uil uil-book-open me-1"></i>
											{t('documentation')}
										</a>
									}
								</div>
							}
						</>
					}
				</div>
			</div>
		</div>
	)
}

export default HealthCheckRow
